import React from 'react';
import { CheckCircle2, CreditCard, PackageSearch, Search, UserCheck } from 'lucide-react';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const formatGroupedNumber = (value, decimals = 2, trimZeros = true) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  let fixed = n.toFixed(decimals);
  if (trimZeros) fixed = fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${grouped}.${decPart}` : grouped;
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `${formatGroupedNumber(value, 2, true)} сом`;
};

const paymentLabel = (status) => {
  const value = String(status || '').trim();
  if (value === 'PROCESSED' || value === 'Оплачено') return 'Оплачено';
  return 'Ожидает оплаты';
};

const messageTextStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const hiddenScrollbarStyle = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

const codeInputStyle = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

const formatDateTimeBishkek = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Asia/Bishkek',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const getState = (order) => {
  if (!order) {
    return {
      title: 'Введите код',
      subtitle: 'Введите 4-значный код выдачи из Telegram.',
      panel: 'border-white/14 bg-white/6',
      icon: <PackageSearch size={34} />,
      iconClass: 'text-[#FACC15] bg-[#FACC15]/12 border-[#FACC15]/25',
    };
  }
  if (String(order.status || '').trim() === 'Получен') {
    return {
      title: 'Заказ уже получен',
      subtitle: 'Если возник вопрос, обратитесь к кассиру.',
      panel: 'border-emerald-400/35 bg-emerald-400/12',
      icon: <CheckCircle2 size={34} />,
      iconClass: 'text-emerald-200 bg-emerald-400/15 border-emerald-300/30',
    };
  }
  if (paymentLabel(order.paymentStatus) === 'Оплачено') {
    return {
      title: 'Заказ успешно оплачен',
      subtitle: 'Ожидайте кассира.',
      panel: 'border-emerald-400/35 bg-emerald-400/12',
      icon: <CheckCircle2 size={34} />,
      iconClass: 'text-emerald-200 bg-emerald-400/15 border-emerald-300/30',
    };
  }
  return {
    title: 'Заказ найден',
    subtitle: 'Ожидает оплаты.',
    panel: 'border-[#FACC15]/35 bg-[#FACC15]/12',
    icon: <CreditCard size={34} />,
    iconClass: 'text-[#FACC15] bg-[#FACC15]/12 border-[#FACC15]/25',
  };
};

const PickupKioskPage = () => {
  const [query, setQuery] = React.useState('');
  const [orders, setOrders] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [paymentQr, setPaymentQr] = React.useState(null);
  const [paymentNow, setPaymentNow] = React.useState(() => Date.now());
  const lastSearchCodeRef = React.useRef('');

  const isPaymentQrActiveNow = React.useCallback(() => {
    const expiresAt = paymentQr?.qr?.expiresAt ? new Date(paymentQr.qr.expiresAt).getTime() : null;
    const expired = !!expiresAt && expiresAt <= Date.now();
    return !!paymentQr?.qr && !expired && paymentLabel(selected?.paymentStatus) !== 'Оплачено';
  }, [paymentQr?.qr, selected?.paymentStatus]);

  const confirmQrExit = React.useCallback(() => {
    if (!isPaymentQrActiveNow()) return true;
    return window.confirm('QR уже создан. Если вы уже оплатили, дождитесь подтверждения. Всё равно вернуться к вводу кода?');
  }, [isPaymentQrActiveNow]);

  const search = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (isPaymentQrActiveNow()) return;
    const code = query.replace(/\D/g, '').slice(0, 4);
    await searchByCode(code);
  };

  const searchByCode = async (code) => {
    setError('');
    setMessage('');
    setSelected(null);
    setPaymentQr(null);
    if (!/^\d{4}$/.test(code)) {
      setError('Введите 4-значный код выдачи');
      return;
    }
    if (loading && lastSearchCodeRef.current === code) return;
    lastSearchCodeRef.current = code;
    setLoading(true);
    try {
      const r = await apiPost('/api/kiosk/orders/search', { query: code });
      const items = Array.isArray(r?.orders) ? r.orders : [];
      setOrders(items);
      setSelected(items[0] || null);
      if (!items.length) setError('Заказ не найден. Проверьте код или обратитесь к кассиру.');
    } catch (e) {
      setError(e?.message || 'Ошибка поиска');
    } finally {
      setLoading(false);
    }
  };

  const appendDigit = (digit) => {
    if (isPaymentQrActiveNow()) return;
    setError('');
    setMessage('');
    setPaymentQr(null);
    setQuery((prev) => {
      const current = String(prev || '').replace(/\D/g, '').slice(0, 4);
      if (current.length >= 4) return current;
      const next = `${current}${digit}`.slice(0, 4);
      if (next !== current) lastSearchCodeRef.current = '';
      return next;
    });
  };

  const removeDigit = () => {
    if (isPaymentQrActiveNow()) return;
    setError('');
    lastSearchCodeRef.current = '';
    setQuery((prev) => String(prev || '').replace(/\D/g, '').slice(0, -1));
  };

  const clearCode = () => {
    if (!confirmQrExit()) return;
    lastSearchCodeRef.current = '';
    setQuery('');
    setSelected(null);
    setOrders([]);
    setPaymentQr(null);
    setError('');
    setMessage('');
  };

  React.useEffect(() => {
    if (isPaymentQrActiveNow()) return undefined;
    const code = query.replace(/\D/g, '').slice(0, 4);
    if (code.length !== 4) return undefined;
    if (lastSearchCodeRef.current === code) return undefined;
    const timer = window.setTimeout(() => {
      searchByCode(code);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [isPaymentQrActiveNow, query]);

  const reset = () => {
    if (!confirmQrExit()) return;
    lastSearchCodeRef.current = '';
    setQuery('');
    setOrders([]);
    setSelected(null);
    setError('');
    setMessage('');
    setPaymentQr(null);
  };

  const createQr = async () => {
    if (!selected?.id) return;
    setError('');
    setMessage('');
    setQrLoading(true);
    try {
      const r = await apiPost('/api/kiosk/payments/qr/create', { orderId: selected.id });
      setPaymentQr(r);
      if (r?.overlap) {
        setMessage('У вас уже есть активный счёт. Сначала оплатите его или дождитесь истечения QR.');
      }
    } catch (e) {
      setError(e?.message || 'Не удалось создать QR для оплаты');
    } finally {
      setQrLoading(false);
    }
  };

  const refreshSelectedByCode = React.useCallback(async () => {
    if (!selected?.pickupCode) return;
    try {
      const r = await apiPost('/api/kiosk/orders/search', { query: selected.pickupCode });
      const items = Array.isArray(r?.orders) ? r.orders : [];
      const next = items.find((item) => String(item.id) === String(selected.id)) || items[0] || null;
      if (!next) return;
      setOrders(items);
      setSelected(next);
      if (paymentLabel(next.paymentStatus) === 'Оплачено') {
        setPaymentQr(null);
        setMessage('Оплата подтверждена. Ожидайте кассира.');
      }
    } catch (_) {
      // keep the current QR screen; the next polling tick can retry
    }
  }, [selected?.id, selected?.pickupCode]);

  const state = getState(selected);
  const codeState = selected
    ? paymentLabel(selected.paymentStatus) === 'Оплачено'
      ? 'paid'
      : 'unpaid'
    : 'idle';
  const paymentQrExpiresAt = paymentQr?.qr?.expiresAt ? new Date(paymentQr.qr.expiresAt).getTime() : null;
  const paymentQrExpired = !!paymentQrExpiresAt && paymentQrExpiresAt <= paymentNow;
  const paymentQrActive = !!paymentQr?.qr && !paymentQrExpired && paymentLabel(selected?.paymentStatus) !== 'Оплачено';

  React.useEffect(() => {
    if (!paymentQr?.qr?.expiresAt) return undefined;
    const timer = window.setInterval(() => setPaymentNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [paymentQr?.qr?.expiresAt]);

  React.useEffect(() => {
    if (!paymentQr?.qr || paymentQrExpired) return undefined;
    if (!selected?.pickupCode || paymentLabel(selected.paymentStatus) === 'Оплачено') return undefined;
    const timer = window.setInterval(refreshSelectedByCode, 5000);
    return () => window.clearInterval(timer);
  }, [paymentQr?.qr, paymentQrExpired, refreshSelectedByCode, selected?.paymentStatus, selected?.pickupCode]);

  return (
    <div className="min-h-screen overflow-hidden bg-[linear-gradient(135deg,#172033,#24324a_52%,#111827)] text-white">
      <div className="pointer-events-none fixed -top-32 -right-20 h-[440px] w-[440px] rounded-full bg-[#FACC15]/24 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-32 -left-20 h-[440px] w-[440px] rounded-full bg-[#60A5FA]/22 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col px-5 py-6">
        <header className="flex items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-[#FACC15]/25 bg-white/8">
            <img src="/logo.png" alt="Japan Express" className="h-12 w-12 object-contain" />
          </div>
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.34em] text-[#FACC15]">Japan Express</div>
            <div className="mt-1 text-3xl font-black tracking-tight">Получение заказа</div>
          </div>
        </header>

        <main className="flex flex-1 items-start py-6">
          <div className={`w-full rounded-[36px] border ${state.panel} p-6 shadow-[0_34px_140px_rgba(0,0,0,0.48)] backdrop-blur-xl`}>
            <div className="grid gap-6 lg:min-h-[720px] lg:grid-cols-[420px_1fr]">
              <section className="flex min-h-[720px] flex-col overflow-hidden rounded-[30px] border border-white/14 bg-white/6 p-5 lg:h-[720px]">
                <div className="flex items-center gap-3">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-3xl border ${state.iconClass}`}>{state.icon}</div>
                  <div>
                    <div className="text-xl font-black">{state.title}</div>
                    <div className="mt-1 text-sm leading-relaxed text-slate-300">{state.subtitle}</div>
                  </div>
                </div>

                <form onSubmit={search} className="mt-5 space-y-2">
                  <input
                    value={query}
                    readOnly
                    tabIndex={-1}
                    onKeyDown={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.preventDefault()}
                    onSelect={(e) => {
                      const target = e.currentTarget;
                      target.setSelectionRange(target.value.length, target.value.length);
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    inputMode="none"
                    placeholder="Введите 4-значный код"
                    style={codeInputStyle}
                    className={`h-14 w-full select-none rounded-3xl border bg-white px-5 text-center text-4xl font-black tracking-wide caret-transparent outline-none placeholder:text-base placeholder:tracking-normal placeholder:text-slate-400 ${
                      codeState === 'paid'
                        ? 'border-emerald-300/80 text-emerald-700 shadow-[0_0_0_4px_rgba(16,185,129,0.18)] focus:border-emerald-300'
                        : codeState === 'unpaid'
                          ? 'border-orange-300/90 text-orange-600 shadow-[0_0_0_4px_rgba(251,146,60,0.18)] focus:border-orange-300'
                          : 'border-white/20 text-slate-950 focus:border-[#FACC15]/70'
                    }`}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                      <button
                        key={digit}
                        type="button"
                        onClick={() => appendDigit(String(digit))}
                        disabled={paymentQrActive}
                        className="h-20 rounded-3xl border border-white/14 bg-white/10 text-4xl font-black text-white shadow-[0_10px_28px_rgba(0,0,0,0.16)] transition hover:bg-white/16 active:translate-y-0.5 active:scale-[0.96] active:bg-[#FACC15] active:text-slate-950 disabled:opacity-45"
                      >
                        {digit}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={clearCode}
                      className="h-20 rounded-3xl border border-white/14 bg-white/8 text-sm font-black text-white shadow-[0_10px_28px_rgba(0,0,0,0.12)] transition hover:bg-white/12 active:translate-y-0.5 active:scale-[0.96] active:bg-white/18"
                    >
                      Очистить
                    </button>
                    <button
                      type="button"
                      onClick={() => appendDigit('0')}
                      disabled={paymentQrActive}
                      className="h-20 rounded-3xl border border-white/14 bg-white/10 text-4xl font-black text-white shadow-[0_10px_28px_rgba(0,0,0,0.16)] transition hover:bg-white/16 active:translate-y-0.5 active:scale-[0.96] active:bg-[#FACC15] active:text-slate-950 disabled:opacity-45"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={removeDigit}
                      disabled={paymentQrActive}
                      className="h-20 rounded-3xl border border-white/14 bg-white/8 text-sm font-black text-white shadow-[0_10px_28px_rgba(0,0,0,0.12)] transition hover:bg-white/12 active:translate-y-0.5 active:scale-[0.96] active:bg-white/18 disabled:opacity-45"
                    >
                      Стереть
                    </button>
                  </div>
                  <button
                    disabled={loading || paymentQrActive || query.replace(/\D/g, '').length !== 4}
                    className="flex h-[52px] w-full items-center justify-center gap-2 rounded-3xl bg-[#FACC15] text-base font-black text-slate-950 disabled:opacity-60"
                  >
                    <Search size={20} /> {loading ? 'Ищем...' : 'Найти заказ'}
                  </button>
                  <div className="h-11">
                    <button
                      type="button"
                      onClick={reset}
                      disabled={!(selected || orders.length || error || message)}
                      className={`h-11 w-full rounded-3xl border border-white/14 bg-white/8 text-sm font-bold text-white transition ${
                        selected || orders.length || error || message
                          ? 'opacity-100'
                          : 'pointer-events-none opacity-0'
                      }`}
                    >
                      {paymentQrActive ? 'Отменить и ввести другой код' : 'Начать заново'}
                    </button>
                  </div>
                </form>

                <div className="mt-auto pt-4">
                  <div className="h-[64px] overflow-hidden">
                  {error ? (
                    <div className="flex h-full items-center rounded-2xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm leading-snug text-red-100">
                      <span className="break-words" style={messageTextStyle}>{error}</span>
                    </div>
                  ) : message ? (
                    <div className="flex h-full items-center rounded-2xl border border-[#FACC15]/25 bg-[#FACC15]/10 px-4 py-3 text-sm leading-snug text-[#FACC15]">
                      <span className="break-words" style={messageTextStyle}>{message}</span>
                    </div>
                  ) : (
                    <div className="h-full" aria-hidden="true" />
                  )}
                  </div>
                </div>
              </section>

              <section className="min-h-[720px] overflow-hidden rounded-[30px] border border-white/14 bg-white/6 p-5 lg:h-[720px]">
                {selected ? (
                  <div className="h-full overflow-y-auto pr-1 [&::-webkit-scrollbar]:hidden" style={hiddenScrollbarStyle}>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Ваш заказ</div>
                    <div className="mt-2 break-all font-mono text-2xl font-black">{selected.trackNumber}</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-3xl border border-white/14 bg-white/8 p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400">Статус</div>
                        <div className="mt-1 text-lg font-black">{selected.status || '—'}</div>
                      </div>
                      <div className="rounded-3xl border border-white/14 bg-white/8 p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400">Оплата</div>
                        <div className="mt-1 text-lg font-black">{paymentLabel(selected.paymentStatus)}</div>
                      </div>
                      <div className="rounded-3xl border border-white/14 bg-white/8 p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400">Сумма</div>
                        <div className="mt-1 text-lg font-black">{formatMoney(selected.amountSom)}</div>
                      </div>
                      <div className="rounded-3xl border border-white/14 bg-white/8 p-3">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400">Количество</div>
                        <div className="mt-1 text-lg font-black">{selected.quantity ?? '—'}</div>
                      </div>
                    </div>

                    {paymentLabel(selected.paymentStatus) === 'Оплачено' && String(selected.status || '').trim() !== 'Получен' ? (
                      <div className="mt-5 rounded-[30px] border border-emerald-300/35 bg-[linear-gradient(135deg,rgba(16,185,129,0.24),rgba(20,184,166,0.12))] p-5 text-center shadow-[0_18px_60px_rgba(16,185,129,0.18)]">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[28px] border border-emerald-200/35 bg-emerald-300/18 text-emerald-100 shadow-[0_14px_38px_rgba(16,185,129,0.22)]">
                          <UserCheck size={42} />
                        </div>
                        <div className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-emerald-100">Оплата прошла</div>
                        {selected.paidAt ? (
                          <div className="mt-2 text-sm font-semibold text-emerald-50/90">
                            Оплачено: {formatDateTimeBishkek(selected.paidAt)}
                          </div>
                        ) : null}
                        <div className="mt-2 text-4xl font-black text-white">Ожидайте кассира</div>
                        <div className="mx-auto mt-3 max-w-sm text-base font-semibold leading-relaxed text-emerald-50">
                          Мы уже показали ваш заказ на экране кассира.
                        </div>
                        <button
                          type="button"
                          onClick={reset}
                          className="mt-5 h-14 w-full rounded-3xl bg-white text-base font-black text-emerald-700 shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition active:translate-y-0.5 active:scale-[0.98]"
                        >
                          ОК
                        </button>
                      </div>
                    ) : null}

                    {paymentLabel(selected.paymentStatus) !== 'Оплачено' ? (
                      <div className="mt-4 rounded-[30px] border border-[#FACC15]/25 bg-[#FACC15]/10 p-4 text-center">
                        <div className="text-lg font-black text-[#FACC15]">Оплата ещё не подтверждена</div>
                        {(!paymentQr?.qr || paymentQrExpired) ? (
                          <button
                            type="button"
                            onClick={createQr}
                            disabled={qrLoading || selected.country !== 'KG'}
                            className="mt-3 h-14 w-full rounded-3xl bg-[#FACC15] text-base font-black text-slate-950 disabled:opacity-60"
                          >
                            {qrLoading ? 'Создаём QR...' : 'Оплатить'}
                          </button>
                        ) : (
                          <div className="mt-3 rounded-2xl border border-white/14 bg-white/8 px-3 py-2 text-sm font-semibold text-white">
                            QR для оплаты создан. Отсканируйте его телефоном.
                          </div>
                        )}
                        {selected.country !== 'KG' ? (
                          <div className="mt-3 text-xs text-slate-300">QR-оплата через банк сейчас доступна только для Кыргызстана.</div>
                        ) : null}
                        {paymentQr?.qr?.qrBase64 && !paymentQrExpired ? (
                          <div className="mt-3 inline-flex rounded-[24px] bg-white p-3">
                            <img
                              src={`data:image/png;base64,${paymentQr.qr.qrBase64}`}
                              alt="QR для оплаты"
                              className="h-[240px] w-[240px] rounded-2xl object-contain"
                            />
                          </div>
                        ) : null}
                        {paymentQr?.qr?.expiresAt && !paymentQrExpired ? (
                          <div className="mt-3 text-xs text-slate-300">QR действует ограниченное время. Если оплата не прошла, создайте QR заново.</div>
                        ) : null}
                        {paymentQrExpired ? (
                          <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm text-red-100">
                            Время QR истекло. Нажмите «Оплатить», чтобы создать новый QR.
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex h-full min-h-[420px] items-center justify-center text-center">
                    <div>
                      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[30px] border border-[#FACC15]/25 bg-[#FACC15]/10 text-[#FACC15]">
                        <PackageSearch size={36} />
                      </div>
                      <div className="mt-5 text-2xl font-black">Введите 4-значный код выдачи</div>
                      <div className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">
                        После поиска здесь появится статус заказа.
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PickupKioskPage;
